import { useLayoutEffect, useMemo, useState } from 'react'
import { useInstantSearch } from 'react-instantsearch'

import { isTrackedSearchError } from 'Algolia/searchClient/createTrackedSearchClient'

type FailureDecision =
  | { mode: 'default-list' }
  | { error: Error; mode: 'error' }

export const useInstantsearchListFallback = () => {
  const [failureDecision, setFailureDecision] = useState<FailureDecision>()
  const instantSearch = useInstantSearch({ catchError: true })
  const { error: instantSearchError } = instantSearch
  const trackedError = isTrackedSearchError(instantSearchError)
    ? instantSearchError
    : undefined
  const isLatestTrackedError = trackedError?.isLatestRequest() === true

  const incomingFailureDecision = useMemo<FailureDecision | undefined>(() => {
    if (instantSearchError && !trackedError) {
      return { error: instantSearchError, mode: 'error' }
    }

    if (!isLatestTrackedError || !trackedError) {
      return undefined
    }

    if (trackedError.hasActiveRefinements) {
      return { error: trackedError, mode: 'error' }
    }

    return { mode: 'default-list' }
  }, [instantSearchError, isLatestTrackedError, trackedError])

  useLayoutEffect(() => {
    if (!incomingFailureDecision) {
      return
    }

    // Persist the first failure before removing search widgets can issue a
    // cleanup request with a different refinement state.
    setFailureDecision(
      currentDecision => currentDecision ?? incomingFailureDecision,
    )
  }, [incomingFailureDecision])

  const effectiveFailureDecision = failureDecision ?? incomingFailureDecision

  const shouldUseDefaultListTable =
    effectiveFailureDecision?.mode === 'default-list'

  let error = instantSearchError

  if (effectiveFailureDecision?.mode === 'error') {
    const { error: failureError } = effectiveFailureDecision

    error = failureError
  } else if (trackedError && !isLatestTrackedError) {
    error = undefined
  }

  return {
    ...instantSearch,
    error,
    shouldUseDefaultListTable,
  }
}
